<template>
  <AddAsset
    :open="isOpen"
    :connecting="loading"
    @update:open="handleUpdateOpen"
    @connect="handleConnect"
    @more-options="handleMoreOptions"
    @describe="handleDescribe"
    @pick-flow="handlePickFlow"
  />
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { ModalClass } from '@types'

import { useAddAssetFlow } from '@/hooks/addAssetFlow'
import { useModalsStore } from '@/store/modals'

import AddAsset from './AddAsset.vue'

/*
 * The adapter between the modal system and the @ds dialog.
 *
 * AddAsset.vue stays a plain Reka dialog driven by `open`, so it can be used
 * anywhere; this component is the only thing that knows about ModalClass. That
 * split matters as more @ds screens arrive — they should not each learn the
 * legacy modal contract.
 */
const props = defineProps<{
  modal: ModalClass<any>
}>()

const modalsStore = useModalsStore()

const isOpen = computed(() => props.modal.isOpened)

/*
 * `hide` and `dismiss` are not the same thing, and the difference is
 * load-bearing.
 *
 * modalsStore.init registers a CLOSE listener that REMOVES the modal from the
 * store's list, so a closed dialog is gone — calling show() on it afterwards
 * sets state on an object ModalFactory no longer renders. hide() moves to
 * HIDDEN, which getOpened also filters out, but fires HIDE rather than CLOSE,
 * so the instance survives and show() brings it back.
 *
 * Anything the user can come back from therefore hides; only a real dismissal
 * closes.
 */
const hide = () => props.modal.hide()
const show = () => props.modal.show(modalsStore.getZIndex())
const dismiss = () => props.modal.close()

// Same dispatch the Welcome screen's inline embed uses. `hide`/`show` are
// this dialog's own state, so cancelling a hand-off returns the user here
// rather than dropping them on the page behind it.
const { loading, handleConnect, handleMoreOptions, handleDescribe, handlePickFlow } =
  useAddAssetFlow({ hide, show, onDismiss: dismiss })

/*
 * Reka owns dismissal — Esc, scrim click, the close button — and reports it as
 * `update:open`. The modal system owns the lifecycle, so that is forwarded to
 * ModalClass rather than tracked separately.
 */
const handleUpdateOpen = (value: boolean) => {
  if (!value) dismiss()
}
</script>
